import React, { useState } from 'react';
import { X, User, Heart, Plus } from 'lucide-react';
import IngredientSearchModal from './IngredientSearchModal';

const DIET_STYLES = [
  { value: 'vegan', label: 'Vegan' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'pescatarian', label: 'Pescatarian' },
  { value: 'keto', label: 'Keto' },
  { value: 'paleo', label: 'Paleo' },
  { value: 'glutenFree', label: 'Gluten-Free' },
  { value: 'dairyFree', label: 'Dairy-Free' },
  { value: 'nutFree', label: 'Nut-Free' },
  { value: 'lowCarb', label: 'Low-Carb' },
  { value: 'halal', label: 'Halal' },
  { value: 'kosher', label: 'Kosher' },
  { value: 'lowFODMAP', label: 'Low-FODMAP' }
];

const AddCareProfileModal = ({ isOpen, onClose, onAdd }) => {
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [dietStyle, setDietStyle] = useState(null);
  const [allergies, setAllergies] = useState([]);
  const [avoidIngredients, setAvoidIngredients] = useState([]);
  const [showIngredientSearch, setShowIngredientSearch] = useState(false);

  const handleSubmit = () => {
    if (!name.trim() || !relationship.trim()) {
      alert('Please enter name and relationship');
      return;
    }

    onAdd({
      name: name.trim(),
      relationship: relationship.trim(),
      dietStyle,
      allergies,
      avoidIngredients
    });

    // Reset form
    setName('');
    setRelationship('');
    setDietStyle(null);
    setAllergies([]);
    setAvoidIngredients([]);
  };

  const toggleDietStyle = (value) => {
    setDietStyle(dietStyle === value ? null : value);
  };

  const handleSelectIngredient = (ingredientName) => {
    if (!avoidIngredients.includes(ingredientName) && !allergies.includes(ingredientName)) {
      setAvoidIngredients([...avoidIngredients, ingredientName]);
    }
  };

  const removeItem = (item, fromAllergies = false) => {
    if (fromAllergies) {
      setAllergies(allergies.filter(a => a !== item));
    } else {
      setAvoidIngredients(avoidIngredients.filter(i => i !== item));
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-lg bg-black/40 backdrop-blur-sm">
        <div className="w-full max-w-2xl max-h-[90vh] bg-gradient-to-br from-[#F5F3EF] to-white rounded-3xl shadow-2xl overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-lg border-b border-[#2D5F4D]/10 flex items-center justify-between flex-shrink-0">
            <h2 className="text-title-2 text-[#2D5F4D] font-serif">Add Family Member</h2>
            <button
              onClick={onClose}
              className="p-sm rounded-full hover:bg-[#2D5F4D]/10 transition-colors"
            >
              <X size={24} className="text-[#2D5F4D]" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-lg">
            <div className="space-y-lg">
              {/* Profile Photo Placeholder */}
              <div className="flex flex-col items-center mb-lg">
                <div className="w-24 h-24 rounded-full bg-[#2D5F4D]/10 flex items-center justify-center mb-md">
                  <User size={48} className="text-[#2D5F4D]" />
                </div>
                <p className="text-caption text-[#8B7355]">Add Profile Photo</p>
              </div>

              {/* Name */}
              <div>
                <label className="block text-caption text-[#8B7355] mb-sm font-medium">Name</label>
                <div className="relative">
                  <User size={20} className="absolute left-md top-1/2 -translate-y-1/2 text-[#2D5F4D]/40" />
                  <input
                    type="text"
                    placeholder="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-12 pr-lg py-md rounded-2xl bg-white border border-[#2D5F4D]/10 focus:border-[#2D5F4D]/30 text-body text-primary placeholder:text-text-tertiary/40 transition-all outline-none"
                  />
                </div>
              </div>

              {/* Relationship */}
              <div>
                <label className="block text-caption text-[#8B7355] mb-sm font-medium">Relationship (e.g., Son, Daughter, Partner)</label>
                <div className="relative">
                  <Heart size={20} className="absolute left-md top-1/2 -translate-y-1/2 text-[#2D5F4D]/40" />
                  <input
                    type="text"
                    placeholder="Relationship"
                    value={relationship}
                    onChange={(e) => setRelationship(e.target.value)}
                    className="w-full pl-12 pr-lg py-md rounded-2xl bg-white border border-[#2D5F4D]/10 focus:border-[#2D5F4D]/30 text-body text-primary placeholder:text-text-tertiary/40 transition-all outline-none"
                  />
                </div>
              </div>

              {/* Dietary Preferences */}
              <div>
                <h3 className="text-title-3 text-[#2D5F4D] font-serif mb-sm">Dietary Preferences</h3>
                <p className="text-caption text-[#8B7355] mb-md">Select dietary preferences like vegan, gluten-free, etc.</p>
                
                <div className="flex flex-wrap gap-sm">
                  {DIET_STYLES.map((style) => (
                    <button
                      key={style.value}
                      onClick={() => toggleDietStyle(style.value)}
                      className={`px-md py-sm rounded-full text-caption font-semibold transition-all border ${
                        dietStyle === style.value
                          ? 'bg-[#2D5F4D] text-white border-[#2D5F4D]'
                          : 'bg-white text-[#2D5F4D] border-[#2D5F4D]/20 hover:border-[#2D5F4D]'
                      }`}
                    >
                      {style.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Restrictions */}
              {avoidIngredients.length > 0 && (
                <div>
                  <h4 className="text-callout font-semibold text-[#2D5F4D] mb-sm">Restrictions</h4>
                  <div className="flex flex-wrap gap-sm">
                    {avoidIngredients.map((item) => (
                      <div
                        key={item}
                        className="flex items-center gap-sm px-md py-sm bg-[#8B7355]/10 border border-[#8B7355]/30 rounded-full"
                      >
                        <span className="text-caption font-semibold text-[#8B7355]">{item}</span>
                        <button
                          onClick={() => removeItem(item, false)}
                          className="hover:bg-[#8B7355]/20 rounded-full p-xs transition-colors"
                        >
                          <X size={14} className="text-[#8B7355]" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => setShowIngredientSearch(true)}
                className="w-full py-md rounded-2xl border-2 border-dashed border-[#2D5F4D]/30 hover:border-[#2D5F4D] hover:bg-[#2D5F4D]/5 text-[#2D5F4D] font-semibold text-callout transition-all flex items-center justify-center gap-sm"
              >
                <Plus size={20} />
                Add Allergen or Restriction
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="p-lg border-t border-[#2D5F4D]/10 flex-shrink-0">
            <button
              onClick={handleSubmit}
              disabled={!name.trim() || !relationship.trim()}
              className="w-full py-lg rounded-2xl bg-gradient-to-r from-[#2D5F4D] to-[#3A6E52] text-white font-semibold text-callout hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Add Member
            </button>
          </div>
        </div>
      </div>

      {/* Ingredient Search Modal */}
      <IngredientSearchModal
        isOpen={showIngredientSearch}
        onClose={() => setShowIngredientSearch(false)}
        onSelect={handleSelectIngredient}
        selectedIngredients={[...allergies, ...avoidIngredients]}
      />
    </>
  );
};

export default AddCareProfileModal;
